import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Video, ExternalLink, Lock, CalendarClock, LogIn, Clock, CalendarDays } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Slots() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null); // { subscription_active, subscription_end, slots:[] }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      if (!token) { setData(null); setLoading(false); return; }
      try {
        const { data: res } = await axios.get(`${API}/my/slots`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setData(res);
      } catch (e) {
        console.error("Slots load error:", e);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  // Demo (not logged in)
  if (!token) {
    return (
      <div className="py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 fade-up">
            <span className="text-xs font-bold uppercase tracking-widest text-[#F97316]">Modalità Demo</span>
            <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-[#0B3D91]" data-testid="slots-title-demo">
              Orari dei corsi
            </h1>
            <p className="mt-2 text-[#5A6A94]">
              Le lezioni si tengono da <strong>lunedì a venerdì</strong>. Iscriviti per ricevere la matricola, l'approvazione dall'admin e il link Google Meet.
            </p>
          </div>
          <div className="card-elevated p-7 text-center">
            <Lock size={40} className="mx-auto text-[#F97316]" />
            <p className="mt-3 text-[#5A6A94]">Accedi con la tua matricola per vedere orari e link di partecipazione.</p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/login" className="btn-outline" data-testid="slots-login-cta">
                <LogIn size={16} /> Accedi
              </Link>
              <Link to="/register" className="btn-accent" data-testid="slots-register-cta">
                Iscriviti — €30 + €20
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="page-bg min-h-[60vh] flex items-center justify-center">
      <div className="card-elevated p-8 text-[#5A6A94]">Caricamento...</div>
    </div>
  );

  const active = !!data?.subscription_active;
  const end = data?.subscription_end;
  const slots = data?.slots || [];

  return (
    <div className="py-14 sm:py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="mb-6 fade-up flex items-center gap-3">
          <div className="w-11 h-11 rounded-full brand-blue-bg text-white flex items-center justify-center font-bold">
            {(user?.nome || "?")[0]}
          </div>
          <div>
            <h1 className="font-display font-extrabold text-2xl text-[#0B3D91]" data-testid="slots-greeting">
              Ciao {user?.nome}!
            </h1>
            <p className="text-xs text-[#5A6A94] flex items-center gap-1.5">
              <span className="font-mono">Matricola: {user?.matricola || "-"}</span>
              {end && (
                <>
                  <span>·</span>
                  <CalendarClock size={13} /> Abbonamento fino al {new Date(end).toLocaleDateString()}
                </>
              )}
            </p>
          </div>
        </div>

        <div className="mb-6 rounded-xl bg-[#EEF4FF] border border-[#0B3D91]/15 px-4 py-3 flex items-center gap-3" data-testid="slots-days-banner">
          <CalendarDays size={18} className="text-[#0B3D91]" />
          <div className="text-sm text-[#0F1E3D]">
            Le lezioni si tengono da <strong>lunedì a venerdì</strong> agli orari sotto indicati.
          </div>
        </div>

        {!active ? (
          <div className="card-elevated p-8 text-center fade-up" data-testid="slots-expired">
            <Lock size={40} className="mx-auto text-[#F97316]" />
            <h2 className="mt-3 font-display font-bold text-xl text-[#0B3D91]">Abbonamento non attivo</h2>
            <p className="mt-1 text-sm text-[#5A6A94]">
              Rinnova la quota di €30 per accedere ai link delle lezioni. Contattaci su WhatsApp per i dettagli.
            </p>
            <Link to="/register" className="btn-accent mt-5 inline-flex">Rinnova ora</Link>
          </div>
        ) : slots.length === 0 ? (
          <div className="card-elevated p-8 text-center fade-up" data-testid="slots-empty">
            <Clock size={40} className="mx-auto text-[#8494B8]" />
            <p className="mt-3 text-[#5A6A94]">Nessun orario pubblicato dall'amministrazione. Riprova più tardi.</p>
          </div>
        ) : (
          <div className="space-y-4 fade-up" data-testid="slots-list">
            {slots.map((s) => (
              <div key={s.id} className="card-elevated p-6 flex flex-col sm:flex-row gap-4 sm:items-center" data-testid={`slot-${s.id}`}>
                <div className="w-14 h-14 rounded-xl brand-blue-bg text-white flex items-center justify-center shrink-0">
                  <Clock size={24} />
                </div>
                <div className="flex-1">
                  <div className="font-display font-extrabold text-2xl text-[#0B3D91]" data-testid={`slot-time-${s.id}`}>
                    {s.time}
                  </div>
                  {s.label && <div className="text-sm text-[#5A6A94] mt-0.5">{s.label}</div>}
                </div>
                {s.meet_link ? (
                  <a
                    href={s.meet_link}
                    target="_blank" rel="noopener noreferrer"
                    className="btn-primary"
                    data-testid={`slot-join-${s.id}`}
                  >
                    <Video size={16} /> Entra <ExternalLink size={12} />
                  </a>
                ) : (
                  <span className="text-sm text-[#8494B8] italic">Link non disponibile</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
